'use client';

import React, { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

import { BiCheck, BiChevronDown, BiX } from 'react-icons/bi';
import { IPlan } from '@/types/Pricing';
import { pricingCategories, pricingPlans } from './PricingData';
import ReactHtmlParser from 'react-html-parser';
import LouiseButton from '@/components/buttons/LouiseButton';

interface PageContentProps {
  locale: string;
}

const SiteFindBestPlanComponent = ({ locale }: PageContentProps) => {
  const { t } = useTranslation(locale);

  const [planType, setPlanType] = useState<'monthly' | 'annually'>('annually');
  const [currency, setCurrency] = useState<'USD' | 'EUR'>(locale.startsWith('en') ? 'USD' : 'EUR');
  const [selectedPlansForComparison, setSelectedPlansForComparison] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  const selectPlan = (plan: IPlan) => {
    window.location.href = `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL as string}/${
      locale.startsWith('en') ? 'en-US' : 'fr-FR'
    }/register?name=${plan.code[planType][currency]}`;
  };

  const plans = pricingPlans(t);
  const categories = pricingCategories(t);
  const [plansData] = useState(() => plans);

  return (
    <section className="tw-min-h-screen tw-bg-gradient-to-br tw-from-slate-50 tw-to-blue-50 tw-py-8 md:tw-py-16 plan-page">
      <div className="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">
        {/* Hero Section */}
        <div className="tw-text-center tw-mb-8 md:tw-mb-16">
          <div className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-3 md:tw-px-4 tw-py-2 tw-bg-gradient-to-r tw-from-emerald-100 tw-to-blue-100 tw-rounded-full tw-mb-4 md:tw-mb-6 tw-flex-wrap tw-text-xs sm:tw-text-sm">
            <span className="tw-text-emerald-600 tw-font-semibold tw-text-center">{t('Pricing/limitedTime')}</span>
            <span className="tw-text-blue-800 tw-font-medium tw-text-center">
              {ReactHtmlParser(t('SiteFindBestPlan/off'))}
            </span>
          </div>

          <h1 className="tw-text-2xl sm:tw-text-3xl md:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-3 md:tw-mb-4 tw-px-2">
            {t('SiteFindBestPlan/find-plan')}
          </h1>
          <h2 className="tw-text-base sm:tw-text-lg md:tw-text-xl tw-text-gray-600 tw-max-w-2xl tw-mx-auto tw-px-4">
            {t('Pricing/subtitle')}
          </h2>
        </div>

        {/* Controls */}
        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-center tw-items-center tw-gap-4 md:tw-gap-8 tw-mb-6 md:tw-mb-8">
          {/* Billing Toggle */}
          <div className="tw-flex tw-items-center tw-gap-3 md:tw-gap-4 tw-order-1 sm:tw-order-none">
            <span
              className={`tw-text-xs sm:tw-text-sm tw-font-medium tw-whitespace-nowrap ${
                planType === 'monthly' ? 'tw-text-gray-900' : 'tw-text-gray-500'
              }`}
            >
              {t('SiteFindBestPlan/btn-02')}
            </span>
            <button
              onClick={() => setPlanType(planType === 'monthly' ? 'annually' : 'monthly')}
              className={`tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-flex-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-2 tw-border-transparent tw-transition-colors tw-duration-200 tw-ease-in-out tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500 tw-focus:ring-offset-2 ${
                planType === 'annually' ? 'tw-bg-blue-600' : 'tw-bg-gray-200'
              }`}
            >
              <span
                className={`tw-pointer-events-none tw-inline-block tw-h-5 tw-w-5 tw-transform tw-rounded-full tw-bg-white tw-shadow tw-ring-0 tw-transition tw-duration-200 tw-ease-in-out ${
                  planType === 'annually' ? 'tw-translate-x-5' : 'tw-translate-x-0'
                }`}
              />
            </button>
            <span
              className={`tw-text-xs sm:tw-text-sm tw-font-medium tw-whitespace-nowrap ${
                planType === 'annually' ? 'tw-text-gray-900' : 'tw-text-gray-500'
              }`}
            >
              {t('SiteFindBestPlan/btn-01')}
              {planType === 'annually' && (
                <span className="tw-ml-2 tw-px-2 tw-py-1 tw-bg-green-100 tw-text-green-700 tw-text-xs tw-font-semibold tw-rounded-full">
                  {t('Pricing/sold')}
                </span>
              )}
            </span>
          </div>

          {/* Currency Toggle */}
          <div className="tw-flex tw-items-center tw-gap-2 md:tw-gap-3 tw-order-2 sm:tw-order-none">
            <span className="tw-text-xs sm:tw-text-sm tw-font-medium tw-text-gray-700 tw-whitespace-nowrap">
              {t('Pricing/currency')}
            </span>
            <div className="tw-flex tw-bg-gray-100 tw-rounded-lg tw-p-1">
              {['USD', 'EUR'].map((curr) => (
                <button
                  key={curr}
                  onClick={() => setCurrency(curr as 'USD' | 'EUR')}
                  className={`tw-px-2 sm:tw-px-3 tw-py-1 tw-text-xs sm:tw-text-sm tw-font-medium tw-rounded-md tw-transition-all tw-duration-200 ${
                    currency === curr
                      ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
                      : 'tw-text-gray-600 hover:tw-text-gray-900'
                  }`}
                >
                  {curr}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison Instructions */}
        {selectedPlansForComparison.length === 0 && (
          <div className="tw-text-center tw-mb-6 md:tw-mb-8 tw-px-4">
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 md:tw-px-4 tw-py-2 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-full">
              <span className="tw-text-blue-600 tw-text-xs sm:tw-text-sm tw-font-medium tw-text-center">
                {t('Pricing/selectForCompare')}
              </span>
            </div>
          </div>
        )}

        {selectedPlansForComparison.length > 0 && (
          <div className="tw-text-center tw-mb-6 md:tw-mb-8 tw-px-4">
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 md:tw-px-4 tw-py-2 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-full">
              <span className="tw-text-green-600 tw-text-xs sm:tw-text-sm tw-font-medium tw-text-center">
                {t('Pricing/selectForCompareSelected', {
                  count: selectedPlansForComparison.length,
                  s: selectedPlansForComparison.length > 1 ? 's' : '',
                })}{' '}
                {t('Pricing/scrollDown')}
              </span>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-4 md:tw-gap-6 lg:tw-gap-8 tw-mb-8 md:tw-mb-16 tw-items-stretch">
          {plansData.map((plan, index) => {
            const isPopular = plan.name.toLowerCase() === 'enterprise';
            const price = plan.price[planType];
            const monthlyPrice = plan.price['monthly'];
            const isSelectedForComparison = selectedPlansForComparison.includes(plan.name);

            return (
              <div
                key={index}
                className={`tw-relative tw-rounded-2xl tw-transition-all tw-duration-300 hover:tw-scale-[1.02] md:hover:tw-scale-105 tw-flex tw-flex-col tw-h-full tw-mx-2 sm:tw-mx-0 ${
                  isSelectedForComparison ? 'tw-ring-2 tw-ring-blue-500 tw-ring-offset-2' : ''
                } ${
                  isPopular
                    ? 'tw-bg-gradient-to-br tw-from-blue-600 tw-to-purple-600 tw-text-white tw-shadow-2xl tw-border-0'
                    : 'tw-bg-white tw-shadow-lg tw-border tw-border-gray-200 tw-hover:shadow-xl'
                }`}
              >
                {isPopular && (
                  <div className="tw-absolute tw--top-3 md:tw--top-4 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-10">
                    <span className="tw-bg-gradient-to-r tw-from-orange-400 tw-to-pink-400 tw-text-white tw-px-3 md:tw-px-4 tw-py-1 md:tw-py-2 tw-rounded-full tw-text-xs md:tw-text-sm tw-font-bold tw-shadow-lg">
                      {t('Pricing/mostPopular')}
                    </span>
                  </div>
                )}

                <div className="tw-p-4 sm:tw-p-6 md:tw-p-8 tw-flex tw-flex-col tw-h-full">
                  {/* Header */}
                  <div className="tw-text-center tw-mb-6 md:tw-mb-8">
                    <h3
                      className={`tw-text-xl md:tw-text-2xl tw-font-bold tw-mb-3 md:tw-mb-4 ${
                        isPopular ? 'tw-text-white' : 'tw-text-gray-900'
                      }`}
                    >
                      {plan.name}
                    </h3>

                    <div className="tw-mb-4 md:tw-mb-6">
                      {price !== null ? (
                        <>
                          <div className="tw-flex tw-items-baseline tw-justify-center tw-gap-1">
                            <span
                              className={`tw-text-3xl md:tw-text-4xl lg:tw-text-5xl tw-font-bold ${
                                isPopular ? 'tw-text-white' : 'tw-text-gray-900'
                              }`}
                            >
                              {currency === 'USD' && '$'}
                              {price}
                              {currency === 'EUR' && '€'}
                            </span>
                            <span
                              className={`tw-text-sm md:tw-text-lg ${
                                isPopular ? 'tw-text-blue-100' : 'tw-text-gray-500'
                              }`}
                            >
                              /{t('Pricing/key37')}
                            </span>
                          </div>

                          {/* Prix mensuel barré - hauteur fixe */}
                          <div className="tw-h-10 md:tw-h-12 tw-flex tw-flex-col tw-justify-center tw-items-center">
                            {planType === 'annually' && monthlyPrice && monthlyPrice > 0 ? (
                              <>
                                <span
                                  className={`tw-text-xs md:tw-text-sm tw-line-through ${
                                    isPopular ? 'tw-text-blue-200' : 'tw-text-gray-400'
                                  }`}
                                >
                                  {currency === 'USD' && '$'}
                                  {monthlyPrice}
                                  {currency === 'EUR' && '€'}/{t('Pricing/month')}
                                </span>
                                <div
                                  className={`tw-text-xs md:tw-text-sm ${
                                    isPopular ? 'tw-text-blue-100' : 'tw-text-green-600'
                                  }`}
                                >
                                  {t('Pricing/key38')}
                                </div>
                              </>
                            ) : null}
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            className={`tw-text-3xl md:tw-text-4xl lg:tw-text-5xl tw-font-bold ${
                              isPopular ? 'tw-text-white' : 'tw-text-gray-900'
                            }`}
                          >
                            {t('Pricing/free')}
                          </div>
                          <div className="tw-h-10 md:tw-h-12"></div>
                        </>
                      )}
                    </div>

                    <div className="tw-w-full tw-mb-4 md:tw-mb-6">
                      {isPopular ? (
                        <LouiseButton
                          color="white"
                          cta={plan.actionBtnText}
                          ctaTarget="register"
                          locale={locale}
                          onClick={() => selectPlan(plan)}
                        />
                      ) : (
                        <LouiseButton
                          color="blue"
                          cta={plan.actionBtnText}
                          ctaTarget="register"
                          locale={locale}
                          onClick={() => selectPlan(plan)}
                        />
                      )}
                    </div>

                    {/* Comparison Checkbox */}
                    <div
                      className={`tw-flex tw-items-center tw-justify-center tw-gap-2 md:tw-gap-3 tw-p-2 md:tw-p-3 tw-rounded-lg tw-border-2 tw-border-dashed tw-transition-colors tw-mb-4 md:tw-mb-6 ${
                        isSelectedForComparison
                          ? isPopular
                            ? 'tw-border-white tw-bg-white/10'
                            : 'tw-border-blue-500 tw-bg-blue-50'
                          : isPopular
                          ? 'tw-border-white/50 hover:tw-border-white'
                          : 'tw-border-gray-300 hover:tw-border-blue-400'
                      }`}
                    >
                      <label className="tw-flex tw-items-center tw-gap-2 md:tw-gap-3 tw-cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelectedForComparison}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPlansForComparison([...selectedPlansForComparison, plan.name]);
                              if (selectedCategory === '') setSelectedCategory('all');
                            } else {
                              setSelectedPlansForComparison(selectedPlansForComparison.filter((p) => p !== plan.name));
                              if (selectedPlansForComparison.length === 1) setSelectedCategory('');
                            }
                          }}
                          className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-white tw-border-gray-300 tw-rounded tw-focus:ring-blue-500 tw-focus:ring-2"
                        />
                        <span
                          className={`tw-text-xs md:tw-text-sm tw-font-medium tw-text-center ${
                            isPopular ? 'tw-text-white' : 'tw-text-gray-700'
                          }`}
                        >
                          {t('Pricing/compareSection/compareThis')}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Key Features - Flex grow pour pousser le contenu */}
                  <div className="tw-flex-grow">
                    <div className="tw-space-y-3 md:tw-space-y-4">
                      {plan.features
                        .filter((f) => f.inSummary === true)
                        .map((feature, idx) => (
                          <div key={idx} className="tw-flex tw-items-start tw-gap-2 md:tw-gap-3">
                            <div
                              className={`tw-flex-shrink-0 tw-w-4 tw-h-4 md:tw-w-5 md:tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mt-0.5 ${
                                isPopular ? 'tw-bg-white/20' : 'tw-bg-blue-100'
                              }`}
                            >
                              {feature.valueType === 'boolean' ? (
                                feature.value ? (
                                  <BiCheck
                                    className={`tw-w-3 tw-h-3 md:tw-w-4 md:tw-h-4 ${
                                      isPopular ? 'tw-text-white' : 'tw-text-blue-600'
                                    }`}
                                  />
                                ) : (
                                  <BiX className="tw-w-3 tw-h-3 md:tw-w-4 md:tw-h-4 tw-text-red-500" />
                                )
                              ) : (
                                <BiCheck
                                  className={`tw-w-3 tw-h-3 md:tw-w-4 md:tw-h-4 ${
                                    isPopular ? 'tw-text-white' : 'tw-text-blue-600'
                                  }`}
                                />
                              )}
                            </div>
                            <span
                              className={`tw-text-xs md:tw-text-sm tw-text-left tw-flex-1 tw-leading-relaxed ${
                                isPopular ? 'tw-text-blue-100' : 'tw-text-gray-600'
                              }`}
                            >
                              {feature.name}
                              {feature.valueType !== 'boolean' ? (
                                <span
                                  className={`tw-font-semibold tw-block tw-mt-1 ${
                                    isPopular ? 'tw-text-white' : 'tw-text-gray-900'
                                  }`}
                                >
                                  {ReactHtmlParser(feature.value)}
                                </span>
                              ) : (
                                ''
                              )}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Feature Category Selector - Mobile responsive */}
        {selectedPlansForComparison.length > 0 && (
          <div className="tw-flex tw-justify-center tw-mb-6 md:tw-mb-8 tw-px-4">
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-gap-3 md:tw-gap-4 tw-bg-white tw-px-4 md:tw-px-6 tw-py-3 md:tw-py-4 tw-rounded-xl tw-shadow-lg tw-border tw-border-gray-200 tw-w-full sm:tw-w-auto">
              <span className="tw-text-sm md:tw-text-base tw-font-semibold tw-text-gray-700 tw-text-center sm:tw-text-left tw-whitespace-nowrap">
                {t('Pricing/compareSection/pricingCompare')}
              </span>
              <div className="tw-relative tw-w-full sm:tw-w-auto">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="tw-appearance-none tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 md:tw-px-4 tw-py-2 tw-pr-8 md:tw-pr-10 tw-text-xs md:tw-text-sm tw-font-medium tw-text-gray-700 tw-cursor-pointer tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500 tw-focus:border-blue-500 tw-w-full sm:tw-min-w-[180px]"
                >
                  <option value="all">{t('Pricing/allFeatures')}</option>
                  {categories
                    .filter((category) => category.value.toLowerCase() !== 'summary')
                    .map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                </select>
                <BiChevronDown className="tw-absolute tw-right-2 md:tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-4 tw-h-4 tw-text-gray-500 tw-pointer-events-none" />
              </div>
              <div className="tw-text-xs md:tw-text-sm tw-text-gray-500 tw-bg-gray-50 tw-px-2 md:tw-px-3 tw-py-1 tw-rounded-full tw-whitespace-nowrap">
                {t('Pricing/plansSelected', {
                  count: selectedPlansForComparison.length,
                  s: selectedPlansForComparison.length > 1 ? 's' : '',
                })}
              </div>
            </div>
          </div>
        )}

        {/* Feature Comparison Table - Mobile responsive */}
        {selectedPlansForComparison.length > 0 && (
          <div className="tw-bg-white tw-rounded-xl md:tw-rounded-2xl tw-shadow-xl tw-overflow-hidden tw-border tw-border-gray-200 tw-mx-2 sm:tw-mx-0">
            <div className="tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 tw-px-4 md:tw-px-8 tw-py-4 md:tw-py-6">
              <h3 className="tw-text-lg md:tw-text-2xl tw-font-bold tw-text-white tw-text-center">
                {selectedCategory === 'all'
                  ? t('Pricing/compareSection/title')
                  : selectedCategory === ''
                  ? t('Pricing/compareSection/overview')
                  : `${categories.find((c) => c.value === selectedCategory)?.label || ''} ${t(
                      'Pricing/compareSection/features'
                    )}`}
              </h3>
              <p className="tw-text-blue-100 tw-text-center tw-text-xs md:tw-text-sm tw-mt-1 md:tw-mt-2">
                {t('Pricing/selectForCompareSelected', {
                  count: selectedPlansForComparison.length,
                  s: selectedPlansForComparison.length > 1 ? 's' : '',
                })}
              </p>
            </div>

            <div className="tw-overflow-x-auto">
              {/* Header */}
              <div
                className="tw-grid tw-gap-0 tw-bg-gray-50"
                style={{
                  gridTemplateColumns: `minmax(200px, 300px) repeat(${selectedPlansForComparison.length}, minmax(150px, 200px))`,
                  minWidth: `${200 + selectedPlansForComparison.length * 150}px`,
                }}
              >
                <div className="tw-px-3 md:tw-px-6 tw-py-3 md:tw-py-4 tw-text-left tw-text-xs md:tw-text-sm tw-font-semibold tw-text-gray-900">
                  {t('Pricing/compareSection/features')}
                </div>
                {plansData
                  .filter((plan) => selectedPlansForComparison.includes(plan.name))
                  .map((plan, index) => (
                    <div
                      key={index}
                      className="tw-px-3 md:tw-px-6 tw-py-3 md:tw-py-4 tw-text-center tw-text-xs md:tw-text-sm tw-font-semibold tw-text-gray-900"
                    >
                      <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 md:tw-gap-2">
                        <span className="tw-break-words">{plan.name}</span>
                        <button
                          onClick={() =>
                            setSelectedPlansForComparison(selectedPlansForComparison.filter((p) => p !== plan.name))
                          }
                          className="tw-text-xs tw-text-red-500 hover:tw-text-red-700 tw-font-normal tw-underline"
                        >
                          {t('Pricing/compareSection/remove')}
                        </button>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Body */}
              <div
                className="tw-divide-y tw-divide-gray-200"
                style={{
                  minWidth: `${200 + selectedPlansForComparison.length * 150}px`,
                }}
              >
                {/* Your existing logic for rendering rows */}
                {selectedCategory === 'all'
                  ? categories
                      .filter((category) => category.value.toLowerCase() !== 'summary')
                      .map((category, categoryIndex) => (
                        <React.Fragment key={categoryIndex}>
                          {/* Category Header */}
                          <div
                            className="tw-grid tw-gap-0 tw-bg-gradient-to-r tw-from-blue-50 tw-to-purple-50"
                            style={{
                              gridTemplateColumns: `minmax(200px, 300px) repeat(${selectedPlansForComparison.length}, minmax(150px, 200px))`,
                            }}
                          >
                            <div className="tw-px-3 md:tw-px-6 tw-py-3 md:tw-py-4 tw-text-xs md:tw-text-sm tw-font-bold tw-text-gray-900 tw-col-span-full">
                              {category.label}
                            </div>
                          </div>

                          {/* Feature Rows */}
                          {plansData[0].features
                            .filter((feature) => feature.category === category.value)
                            .map((feature, featureIndex) => (
                              <div
                                key={featureIndex}
                                className="tw-grid tw-gap-0 hover:tw-bg-gray-50 tw-transition-colors"
                                style={{
                                  gridTemplateColumns: `minmax(200px, 300px) repeat(${selectedPlansForComparison.length}, minmax(150px, 200px))`,
                                }}
                              >
                                <div className="tw-px-3 md:tw-px-6 tw-py-3 md:tw-py-4 tw-text-xs md:tw-text-sm tw-text-gray-700 tw-break-words">
                                  {feature.name}
                                </div>
                                {plansData
                                  .filter((plan) => selectedPlansForComparison.includes(plan.name))
                                  .map((plan, planIndex) => {
                                    const planFeature = plan.features.find((f) => f.name === feature.name);
                                    return (
                                      <div
                                        key={planIndex}
                                        className="tw-px-3 md:tw-px-6 tw-py-3 md:tw-py-4 tw-text-center tw-flex tw-items-center tw-justify-center"
                                      >
                                        {planFeature?.valueType === 'boolean' ? (
                                          planFeature.value ? (
                                            <BiCheck className="tw-w-5 tw-h-5 md:tw-w-6 md:tw-h-6 tw-text-green-500 tw-flex-shrink-0" />
                                          ) : (
                                            <BiX className="tw-w-5 tw-h-5 md:tw-w-6 md:tw-h-6 tw-text-red-500 tw-flex-shrink-0" />
                                          )
                                        ) : (
                                          <span className="tw-text-xs md:tw-text-sm tw-font-semibold tw-text-gray-900 tw-break-words tw-text-center tw-leading-tight">
                                            {planFeature?.value || '-'}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            ))}
                        </React.Fragment>
                      ))
                  : // Handle other cases (overview and specific category)
                    (selectedCategory === ''
                      ? plansData[0].features.filter((f) => f.category === categories[0]?.value).slice(0, 10)
                      : plansData[0].features.filter((feature) => feature.category === selectedCategory)
                    ).map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="tw-grid tw-gap-0 hover:tw-bg-gray-50 tw-transition-colors"
                        style={{
                          gridTemplateColumns: `minmax(200px, 300px) repeat(${selectedPlansForComparison.length}, minmax(150px, 200px))`,
                        }}
                      >
                        <div className="tw-px-3 md:tw-px-6 tw-py-3 md:tw-py-4 tw-text-xs md:tw-text-sm tw-text-gray-700 tw-break-words">
                          {feature.name}
                        </div>
                        {plansData
                          .filter((plan) => selectedPlansForComparison.includes(plan.name))
                          .map((plan, planIndex) => {
                            const planFeature = plan.features.find((f) => f.name === feature.name);
                            return (
                              <div
                                key={planIndex}
                                className="tw-px-3 md:tw-px-6 tw-py-3 md:tw-py-4 tw-text-center tw-flex tw-items-center tw-justify-center"
                              >
                                {planFeature?.valueType === 'boolean' ? (
                                  planFeature.value ? (
                                    <BiCheck className="tw-w-5 tw-h-5 md:tw-w-6 md:tw-h-6 tw-text-green-500 tw-flex-shrink-0" />
                                  ) : (
                                    <BiX className="tw-w-5 tw-h-5 md:tw-w-6 md:tw-h-6 tw-text-red-500 tw-flex-shrink-0" />
                                  )
                                ) : (
                                  <span className="tw-text-xs md:tw-text-sm tw-font-semibold tw-text-gray-900 tw-break-words tw-text-center tw-leading-tight">
                                    {planFeature?.value || '-'}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    ))}
              </div>

              {/* CTAs alignés avec les colonnes sélectionnées */}
              <div
                className="tw-bg-gray-50 tw-hidden lg:tw-block"
                style={{
                  minWidth: `${200 + selectedPlansForComparison.length * 150}px`,
                }}
              >
                <div
                  className="tw-grid tw-gap-0"
                  style={{
                    gridTemplateColumns: `minmax(200px, 300px) repeat(${selectedPlansForComparison.length}, minmax(150px, 200px))`,
                  }}
                >
                  <div className="tw-px-3 md:tw-px-6 tw-py-4 md:tw-py-6 tw-text-left tw-text-xs md:tw-text-sm tw-font-semibold tw-text-gray-900 tw-flex tw-items-center">
                    {t('Pricing/readyChoose')}
                  </div>
                  {plansData
                    .filter((plan) => selectedPlansForComparison.includes(plan.name))
                    .map((plan, index) => (
                      <div
                        key={index}
                        className="tw-px-3 md:tw-px-6 tw-py-4 md:tw-py-6 tw-text-center tw-flex tw-items-center tw-justify-center"
                      >
                        <div className="tw-w-full tw-max-w-[140px] tw-text-xs">
                          <LouiseButton
                            color="blue"
                            cta={plan.actionBtnText}
                            ctaTarget="register"
                            locale={locale}
                            onClick={() => selectPlan(plan)}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trust Indicators */}
        <div className="tw-text-center tw-mt-8 md:tw-mt-16 tw-pt-8 md:tw-pt-16 tw-border-t tw-border-gray-200">
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-flex-wrap tw-justify-center tw-items-center tw-gap-4 md:tw-gap-8 tw-text-gray-500">
            <div className="tw-flex tw-items-center tw-gap-2">
              <BiCheck className="tw-w-4 tw-h-4 md:tw-w-5 md:tw-h-5 tw-text-green-500" />
              <span className="tw-text-xs md:tw-text-sm tw-text-center">{t('Pricing/14FreeTrial')}</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <BiCheck className="tw-w-4 tw-h-4 md:tw-w-5 md:tw-h-5 tw-text-green-500" />
              <span className="tw-text-xs md:tw-text-sm tw-text-center">{t('Pricing/noFees')}</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <BiCheck className="tw-w-4 tw-h-4 md:tw-w-5 md:tw-h-5 tw-text-green-500" />
              <span className="tw-text-xs md:tw-text-sm tw-text-center">{t('Pricing/cancelAnytime')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SiteFindBestPlanComponent;
